import { Injectable } from '@angular/core';
import { ColumnType, Dataset, FilterType, Schema, SelectionMode } from '@mode-vegas/vegas';
import { ReportFilterControlHelpers } from '@mode/filters/util';
import { ModelState, ReportDataset } from '@mode/legacy/types-app-state';
import { ReadonlyCollection, ReportTypes } from '@mode/shared/contract-common';
import { isValid } from 'date-fns';
import DatasetService from './dataset-service';

export function loadFilteredDataset(
  reportModel: ModelState,
  filterControls: ReportTypes.ReportFilterControl[]
): PromiseLike<ReportDataset> {
  let dataset: ReportDataset = null;

  try {
    if (reportModel.state !== 'succeeded') {
      // just return a dummy dataset for status purposes
      dataset = new ReportDataset({
        id: reportModel.token,
        token: reportModel.token,
        isLoaded: true,
        state: reportModel.state,
        query_token: reportModel.token,
        queryName: reportModel.queryName,
        reportDataUrl: reportModel.reportDataUrl,
        reportQueryUrl: reportModel.reportQueryUrl,
        oversized: reportModel.oversized,
        content_length: reportModel.content_length,
        csv: reportModel.csv,
        content: [],
        columns: [],
      });
    } else {
      if (filterControls.length === 0) {
        const ds = reportModel.model.engine.dataset;

        dataset = new ReportDataset({
          id: reportModel.token,
          token: reportModel.token,
          isLoaded: true,
          state: reportModel.state,
          query_token: reportModel.token,
          queryName: reportModel.queryName,
          reportDataUrl: reportModel.reportDataUrl,
          reportQueryUrl: reportModel.reportQueryUrl,
          oversized: reportModel.oversized,
          csv: reportModel.csv,
          content_length: reportModel.content_length,
          count: reportModel.model.engine.dataset.data.length,
          columns: buildDatasetColumns(ds),
          content: buildDatasetContent(ds.data, ds.schema),
        });
      }
      // there are filters, so do some filtering
      else {
        const model = reportModel.model;
        const filterEngine = model.onFilter((query) => {
          for (const filterControl of filterControls) {
            const field = model.buildViewCalcField(filterControl.formula, filterControl.varType, filterControl.token);
            if (!field.formula.isValid) {
              continue;
            }

            const encFilter = ReportFilterControlHelpers.toEncFilterField(filterControl);

            switch (encFilter.filterType) {
              case FilterType.DiscreteBoolean:
              case FilterType.Discrete: {
                if (encFilter.selection.mode !== SelectionMode.ALL) {
                  query.where(
                    model.buildDiscreteFilter(field, {
                      selected: new Set(encFilter.selection.selected),
                    })
                  );
                }
                break;
              }
              case FilterType.ContinuousDate: {
                const { min, max } = encFilter.range;
                if (Number.isSafeInteger(min) || Number.isSafeInteger(max)) {
                  query.where(
                    model.buildContinuousFilter(field, {
                      includeNulls: encFilter.includeNulls,
                      includeMin: encFilter.includeMin,
                      includeMax: encFilter.includeMax,
                      min,
                      max,
                    })
                  );
                }
                break;
              }
              case FilterType.Continuous: {
                const { min, max } = encFilter.range;
                if (Number.isFinite(min) || Number.isFinite(max)) {
                  query.where(
                    model.buildContinuousFilter(field, {
                      includeNulls: encFilter.includeNulls,
                      includeMin: encFilter.includeMin,
                      includeMax: encFilter.includeMax,
                      min,
                      max,
                    })
                  );
                }
                break;
              }
            }
          }
        });

        const result = filterEngine.execute();
        const ds = reportModel.model.engine.dataset;

        dataset = new ReportDataset({
          id: reportModel.token,
          token: reportModel.token,
          isLoaded: true,
          state: reportModel.state,
          query_token: reportModel.token,
          queryName: reportModel.queryName,
          reportDataUrl: reportModel.reportDataUrl,
          reportQueryUrl: reportModel.reportQueryUrl,
          oversized: reportModel.oversized,
          csv: reportModel.csv,
          content_length: reportModel.content_length,
          count: result.result.size,
          columns: buildDatasetColumns(ds),
          content: buildDatasetContent(result.result.data, ds.schema),
        });
      }
    }
  } catch (e) {
    return Promise.reject(e);
  }

  return Promise.resolve(dataset);
}

// TODO: unsure of the solution, but report filtering should have a different pattern.
// Just going to have this be an exported function so it can be reused outside of injected services.
export function loadFilteredDatasets(
  reportModels: ReadonlyCollection<ModelState>,
  filterControls: ReportTypes.ReportFilterControl[]
): PromiseLike<ReportDataset[]> {
  return Promise.all(reportModels.map((model) => loadFilteredDataset(model, filterControls)));
}

function buildDatasetColumns(dataset: Dataset): any {
  const result = new Array<any>();

  for (const column of dataset.schema.columns) {
    switch (column.type) {
      case ColumnType.FLOAT:
        result.push({ type: 'float', name: column.name });
        break;
      case ColumnType.INTEGER:
        result.push({ type: 'integer', name: column.name });
        break;
      case ColumnType.DATE:
      case ColumnType.DATETIME:
        result.push({ type: 'datetime', name: column.name });
        break;
      case ColumnType.STRING:
        result.push({ type: 'string', name: column.name });
        break;
      case ColumnType.BOOLEAN:
        result.push({ type: 'boolean', name: column.name });
        break;
    }
  }

  return result;
}

function buildDatasetContent(data: object[], schema: Schema): object[] {
  const dateColumns = new Array<string>();
  for (const column of schema.columns) {
    switch (column.type) {
      case 'DATE':
      case 'DATETIME':
        dateColumns.push(column.name);
        break;
    }
  }

  if (dateColumns.length === 0) {
    return data;
  } else {
    const content = new Array(data.length);

    let rowNum = 0;
    for (const datum of data) {
      const row: { [index: string]: any } = {};

      Object.assign(row, datum); // Copy Data

      let currValue: any;
      for (const columnName of dateColumns) {
        currValue = row[columnName];
        if (typeof row[columnName] === 'undefined' || currValue === null) {
          row[columnName] = null;
        } else {
          currValue = new Date(row[columnName]);
          row[columnName] = isValid(currValue) ? currValue.toISOString() : null;
        }
      }

      content[rowNum++] = row;
    }
    return content;
  }
}

@Injectable({ providedIn: 'root' })
export class ReportDatasetService {
  constructor(private datasetService: DatasetService) {}

  loadFilteredDatasets(
    reportModels: ReadonlyCollection<ModelState>,
    filterControls: ReportTypes.ReportFilterControl[]
  ): PromiseLike<ReportDataset[]> {
    return loadFilteredDatasets(reportModels, filterControls).then((reportDatasets) => {
      // In phantom world, datasets won't be set, and it doesn't need it to be
      if (this.datasetService.datasets) {
        // Don't reset the entire datasets pointer since that will force refreshes
        reportDatasets.forEach((ds) => {
          this.datasetService.datasets[ds.query_token] = ds;
        });
      }

      return reportDatasets;
    });
  }

  loadFilteredDataset(
    reportModel: ModelState,
    filterControls: ReportTypes.ReportFilterControl[]
  ): PromiseLike<ReportDataset> {
    return loadFilteredDataset(reportModel, filterControls).then((reportDataset) => {
      if (this.datasetService.datasets) {
        this.datasetService.datasets[reportDataset.query_token] = reportDataset;
      }

      return reportDataset;
    });
  }
}
